// Story BP-5: paywall gate — wrapper declarativo + hook imperativo.
//
// Duas APIs:
//   1. <UpgradeGate feature="pdf.individual" tierRequired="pro" reason="..." ...>
//        <Button onClick={handler}>Exportar</Button>
//      </UpgradeGate>
//      → Quando gate FAIL: intercepta click e abre UpgradeModal.
//      → Quando gate PASS: renderiza children inalterado (click passa normal).
//
//   2. const { ensureFeature, ensureLimit } = useUpgradeGate();
//      async function handler() {
//        if (!(await ensureFeature('pdf.individual', 'pro', '...'))) return;
//        // proceed
//      }
//      → Uso em async handlers onde wrapping JSX não cabe.
//
// Depende do UpgradeGateProvider mounted no App (abriga o singleton Modal state).

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  cloneElement,
  isValidElement,
  MouseEvent,
  ReactElement,
} from 'react';
import { usePlan } from '../../hooks/usePlan';
import type { PlanTier, FeatureKey, LimitKey } from '../../lib/plans/types';
import { logService } from '../../lib/logService';
import { UpgradeModal } from './UpgradeModal';

type TierTarget = Exclude<PlanTier, 'free'>;

interface ModalState {
  reason: string;
  tierRequired: TierTarget;
  ctaText?: string;
  feature?: FeatureKey | string;
}

interface UpgradeGateContextValue {
  openModal: (state: ModalState) => void;
  closeModal: () => void;
}

const UpgradeGateContext = createContext<UpgradeGateContextValue | undefined>(undefined);

// ─── Provider ──────────────────────────────────────────────────

export const UpgradeGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const openModal = useCallback((state: ModalState) => {
    setModalState(state);
  }, []);

  const closeModal = useCallback(() => {
    setModalState(null);
  }, []);

  const value = useMemo(() => ({ openModal, closeModal }), [openModal, closeModal]);

  return (
    <UpgradeGateContext.Provider value={value}>
      {children}
      {modalState && (
        <UpgradeModal
          isOpen={true}
          onClose={closeModal}
          reason={modalState.reason}
          tierRequired={modalState.tierRequired}
          ctaText={modalState.ctaText}
          feature={modalState.feature}
        />
      )}
    </UpgradeGateContext.Provider>
  );
};

const useUpgradeGateContext = (): UpgradeGateContextValue => {
  const ctx = useContext(UpgradeGateContext);
  if (!ctx) {
    throw new Error('UpgradeGate must be used within UpgradeGateProvider');
  }
  return ctx;
};

// ─── Hook público pra dispatch server-side-driven (BP-7) ─────────
// Complementa `useUpgradeGate` (que checa client state) expondo acesso direto
// ao openModal pra fluxos onde o server retornou 402 e o client só precisa abrir
// o modal com o state fornecido pelo payload — sem re-checagem de predicates.

export interface UseUpgradeModalReturn {
  openUpgradeModal: (state: ModalState) => void;
  closeUpgradeModal: () => void;
}

export const useUpgradeModal = (): UseUpgradeModalReturn => {
  const { openModal, closeModal } = useUpgradeGateContext();
  return {
    openUpgradeModal: openModal,
    closeUpgradeModal: closeModal,
  };
};

// ─── Hook imperativo ──────────────────────────────────────────

interface EnsureFeatureOptions {
  ctaText?: string;
}

interface EnsureLimitOptions extends EnsureFeatureOptions {
  /** Tier pra qual upgradar (default: 'pro') */
  tierRequired?: TierTarget;
}

export interface UseUpgradeGateReturn {
  /** Retorna true se user PODE usar a feature; false se modal foi aberto (caller aborta). */
  ensureFeature: (
    feature: FeatureKey,
    tierRequired: TierTarget,
    reason: string,
    options?: EnsureFeatureOptions
  ) => boolean;
  /** Retorna true se currentCount está within limit; false se modal foi aberto. */
  ensureLimit: (
    limit: LimitKey,
    currentCount: number,
    reason: string,
    options?: EnsureLimitOptions
  ) => boolean;
}

export const useUpgradeGate = (): UseUpgradeGateReturn => {
  const { can, withinLimit, tier } = usePlan();
  const { openModal } = useUpgradeGateContext();

  const ensureFeature = useCallback<UseUpgradeGateReturn['ensureFeature']>(
    (feature, tierRequired, reason, options) => {
      if (can(feature)) return true;
      logService.info('upgrade_intent', `gate_triggered ${feature} → ${tierRequired}`, {
        feature,
        tier_current: tier,
        tier_required: tierRequired,
        cta_clicked: false,
      });
      openModal({ reason, tierRequired, ctaText: options?.ctaText, feature });
      return false;
    },
    [can, openModal, tier]
  );

  const ensureLimit = useCallback<UseUpgradeGateReturn['ensureLimit']>(
    (limit, currentCount, reason, options) => {
      if (withinLimit(limit, currentCount)) return true;
      const tierRequired = options?.tierRequired ?? 'pro';
      logService.info('upgrade_intent', `gate_triggered limit ${limit} → ${tierRequired}`, {
        feature: `limit:${limit}`,
        tier_current: tier,
        tier_required: tierRequired,
        cta_clicked: false,
        current_count: currentCount,
      });
      openModal({ reason, tierRequired, ctaText: options?.ctaText, feature: `limit:${limit}` });
      return false;
    },
    [withinLimit, openModal, tier]
  );

  return { ensureFeature, ensureLimit };
};

// ─── Wrapper declarativo ──────────────────────────────────────

type FeatureGateProps = {
  feature: FeatureKey;
  tierRequired: TierTarget;
  reason: string;
  ctaText?: string;
  limit?: never;
  currentCount?: never;
  children: ReactElement<{ onClick?: (e: MouseEvent) => void; disabled?: boolean }>;
};

type LimitGateProps = {
  limit: LimitKey;
  currentCount: number;
  tierRequired: TierTarget;
  reason: string;
  ctaText?: string;
  feature?: never;
  children: ReactElement<{ onClick?: (e: MouseEvent) => void; disabled?: boolean }>;
};

export type UpgradeGateProps = FeatureGateProps | LimitGateProps;

export const UpgradeGate: React.FC<UpgradeGateProps> = (props) => {
  const { can, withinLimit, tier } = usePlan();
  const { openModal } = useUpgradeGateContext();

  const isFeatureGate = 'feature' in props && props.feature !== undefined;
  const isLimitGate = 'limit' in props && props.limit !== undefined;

  if (!isFeatureGate && !isLimitGate) {
    if (typeof console !== 'undefined') {
      console.error('[UpgradeGate] Require exactly one of `feature` or `limit` prop.');
    }
    return <>{props.children}</>;
  }

  const passes = isFeatureGate
    ? can(props.feature as FeatureKey)
    : withinLimit(props.limit as LimitKey, props.currentCount as number);

  if (passes) {
    return <>{props.children}</>;
  }

  // Gate FAIL — intercepta click, abre modal
  if (!isValidElement(props.children)) {
    return <>{props.children}</>;
  }

  const featureRef = isFeatureGate
    ? (props.feature as FeatureKey)
    : `limit:${props.limit as LimitKey}`;

  const originalOnClick = props.children.props.onClick;

  const handleInterceptedClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    logService.info('upgrade_intent', `gate_triggered ${featureRef} → ${props.tierRequired}`, {
      feature: featureRef,
      tier_current: tier,
      tier_required: props.tierRequired,
      cta_clicked: false,
    });
    openModal({
      reason: props.reason,
      tierRequired: props.tierRequired,
      ctaText: props.ctaText,
      feature: featureRef,
    });
    // originalOnClick deliberadamente não é chamado — gate bloqueia a ação
    void originalOnClick;
  };

  return cloneElement(props.children, {
    onClick: handleInterceptedClick,
  });
};
